import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { CHANNEL_LABELS, reminderLabel, WHEN_LABELS, type Reminder, type ReminderChannel } from '../company-tasks/TaskReminders';
import { PRIVATE_BASE, PRIVATE_KEY } from './types';

export type PrivateWhen = 'hour' | 'tomorrow' | 'due' | 'day_before' | 'custom';
export const PRIVATE_WHEN_LABELS: Record<PrivateWhen, string> = {
  hour: WHEN_LABELS.hour,
  tomorrow: WHEN_LABELS.tomorrow,
  due: 'W dniu terminu (o godzinie zadania albo o 9:00)',
  day_before: WHEN_LABELS.day_before,
  custom: WHEN_LABELS.custom,
};
/** The planned reminders, the channels that reach me, and those of the last one that came (null: none came yet). */
type Answer = { reminders: Reminder[]; channels: Record<ReminderChannel, boolean>; lastSentChannels: ReminderChannel[] | null };

/** The choices of "Przypomnij…" for a task with or without a due day. */
export const whensFor = (hasDueDay: boolean): PrivateWhen[] => (Object.keys(PRIVATE_WHEN_LABELS) as PrivateWhen[]).filter((w) => hasDueDay || (w !== 'due' && w !== 'day_before'));

/**
 * "Przypomnienia" of my task: only to me — the bell, and e-mail, SMS, push when chosen; "Odłóż" once a reminder came.
 * No "Przypomnij teraz" (reminding oneself now makes no sense).
 */
export function PrivateReminders({ taskId, hasDueDay }: { taskId: string; hasDueDay: boolean }) {
  const queryClient = useQueryClient();
  const key = [...PRIVATE_KEY, 'reminders', taskId];
  const list = useQuery({ queryKey: key, queryFn: () => api<Answer>(`${PRIVATE_BASE}/${taskId}/reminders`) });
  const [channels, setChannels] = useState<ReminderChannel[]>(['push']);
  const [planning, setPlanning] = useState(false);
  const [when, setWhen] = useState<PrivateWhen>(hasDueDay ? 'due' : 'tomorrow');
  const [day, setDay] = useState('');
  const [time, setTime] = useState('09:00');
  const [snoozed, setSnoozed] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const available = list.data?.channels ?? { email: false, sms: false, push: false };
  const chosen = channels.filter((c) => available[c]);
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Reminder>(`${PRIVATE_BASE}/${taskId}/reminders`, { method: 'POST', body: { ...body, channels: chosen } }),
    onSuccess: () => setPlanning(false),
    onSettled: refresh,
  });
  const snooze = useMutation({
    mutationFn: (w: 'hour' | 'tomorrow') => api<Reminder>(`${PRIVATE_BASE}/${taskId}/snooze`, { method: 'POST', body: { when: w } }),
    onSuccess: (r) => setSnoozed(`Odłożone: ${reminderLabel(r)}`),
    onSettled: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`${PRIVATE_BASE}/${taskId}/reminders/${id}`, { method: 'DELETE' }), onSettled: refresh });
  const error = add.error ?? snooze.error ?? remove.error;
  const issues = error instanceof ApiError ? fieldIssues(error) : [];

  return (
    <section
      className="ct-reminders"
      aria-labelledby={`pt-rem-${taskId}`}
      data-feature-id="private-tasks.reminders"
      // inside the task's form: Enter in the day or time must not save and close the task
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) e.preventDefault();
      }}
    >
      <h3 id={`pt-rem-${taskId}`}>Przypomnienia</h3>
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.data && (
        <>
          {list.data.lastSentChannels !== null && (
            <div className="ct-rem-actions pt-snooze">
              <span className="muted">{snoozed ?? 'Przypomnienie przyszło. Odłóż:'}</span>
              {!snoozed && (
                <>
                  <button type="button" className="btn small" disabled={snooze.isPending} onClick={() => snooze.mutate('hour')}>
                    Za godzinę
                  </button>
                  <button type="button" className="btn small" disabled={snooze.isPending} onClick={() => snooze.mutate('tomorrow')}>
                    Jutro o 9:00
                  </button>
                </>
              )}
            </div>
          )}
          <div className="ct-rem-actions">
            <button type="button" className="btn small ghost" aria-expanded={planning} onClick={() => setPlanning((v) => !v)}>
              Przypomnij…
            </button>
          </div>
          {planning && (
            <div className="ct-rem-plan" role="group" aria-label="Zaplanuj przypomnienie">
              <fieldset className="ct-rem-channels">
                <legend>Gdzie przypomnieć</legend>
                <label className="ct-check">
                  <input type="checkbox" checked disabled /> Dzwonek
                </label>
                {(Object.keys(CHANNEL_LABELS) as ReminderChannel[])
                  .filter((c) => available[c])
                  .map((c) => (
                    <label key={c} className="ct-check">
                      <input type="checkbox" checked={channels.includes(c)} onChange={(e) => setChannels((cs) => (e.target.checked ? [...cs, c] : cs.filter((x) => x !== c)))} /> {CHANNEL_LABELS[c]}
                    </label>
                  ))}
              </fieldset>
              <label className="field">
                <span>Kiedy</span>
                <select value={when} onChange={(e) => setWhen(e.target.value as PrivateWhen)}>
                  {whensFor(hasDueDay).map((w) => (
                    <option key={w} value={w}>
                      {PRIVATE_WHEN_LABELS[w]}
                    </option>
                  ))}
                </select>
              </label>
              {when === 'custom' && (
                <div className="ct-row">
                  <label className="field">
                    <span>Dzień</span>
                    <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Godzina</span>
                    <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                  </label>
                </div>
              )}
              <button type="button" className="btn small primary" disabled={add.isPending} onClick={() => add.mutate({ when, ...(when === 'custom' ? { day, time } : {}) })}>
                Dodaj przypomnienie
              </button>
            </div>
          )}
          {list.data.reminders.length > 0 && (
            <ul className="ct-rem-list" aria-label="Zaplanowane przypomnienia">
              {list.data.reminders.map((r) => (
                <li key={r.id}>
                  <span>{reminderLabel(r)}</span>
                  <button type="button" className="btn ghost small" disabled={remove.isPending} onClick={() => remove.mutate(r.id)} aria-label={`Usuń przypomnienie ${reminderLabel(r)}`}>
                    Usuń
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {error && (
        <p className="alert" role="alert">
          {issues.length > 0 ? issues.map((i) => i.message).join(' ') : error instanceof ApiError ? error.message : 'Nie udało się'}
        </p>
      )}
    </section>
  );
}
